import IEventService from '../../../../eventService/IEventService';
import { getServices } from '../../../../../infra/startup';
import {
  EventServiceCallBackType,
  ListenerHandlerType,
  OnboardingAgentEvents
} from '../../../../eventService/types';
import { internalLogger } from '../../../../../interface/v1/clientOsLogger';

class EventManager {
  private _eventService: IEventService;

  constructor() {
    const services = getServices();
    this._eventService = services?.eventService;
  }

  public subscribeToEvent(
    eventName: string,
    callback: EventServiceCallBackType
  ): Promise<ListenerHandlerType> {
    return this._eventService.subscribe(eventName, callback);
  }

  public sendOnboardingFailedToLaunchEvent(): void {
    this._eventService.publish(
      OnboardingAgentEvents.onboardingAgentFailedToLaunch,
      undefined
    );
  }

  public sendWebOnboardingStartedEvent(appSessionId: string | undefined): void {
    internalLogger?.log?.('onboarding-agent-event: WebOnboardingStarted', {
      appSessionId
    });
    this._eventService.publish(
      this._eventService.eventNames.webOnboardingStarted,
      { appSessionId }
    );
  }

  public sendWebOnboardingStageStartedEvent(
    appSessionId: string | undefined,
    serviceId?: string
  ): void {
    internalLogger?.log?.('onboarding-agent-event: WebOnboardingStageStarted', {
      appSessionId,
      serviceId
    });
    this._eventService.publish(
      this._eventService.eventNames.webOnboardingStageStarted,
      {
        appSessionId,
        serviceId
      }
    );
  }

  /**
   * Publishes a lightweight public event for stage completion.
   *
   * Intentionally capped payload:
   * - Exposes only appSessionId, serviceId and result/result.xCorrelationId.
   * - Does NOT forward resultData.output or resultData.errorInfo.
   *
   * Rationale:
   * - This event is a broad notification hook consumed outside OnboardingAgent
   *   boundaries (external subscribers via eventService).
   * - Sensitive/verbose close payload details must remain in the OD update path
   *   (SessionManager.getNextOnboardingDirectorStage), not in public event traffic.
   */
  public sendOnboardingStageFinishedEvent(
    appSessionId: string,
    serviceId: string,
    resultData: {
      result: 'success' | 'failed' | 'cancelled';
      output?: Record<string, any>;
      xCorrelationId: string;
      errorInfo?: {
        errorCode?: string;
        errorDescription?: string;
      };
    },
    xCorrelationId: string
  ): void {
    // Keep the event contract minimal and stable for external listeners.
    const payload = {
      appSessionId,
      serviceId,
      result: {
        result: resultData?.result,
        xCorrelationId: resultData?.xCorrelationId || xCorrelationId
      }
    };
    internalLogger?.log?.(
      'onboarding-agent-event: WebOnboardingStageFinished',
      payload
    );
    this._eventService.publish(
      this._eventService.eventNames.webOnboardingStageFinished,
      payload
    );
  }

  public sendWebOnboardingFinishedEvent(
    appSessionId: string | undefined,
    serviceId: string | undefined,
    result: 'success' | 'failed' | 'cancelled'
  ): void {
    const payload = {
      appSessionId,
      serviceId,
      result
    };
    internalLogger?.log?.(
      'onboarding-agent-event: WebOnboardingFinished',
      payload
    );
    this._eventService.publish(
      this._eventService.eventNames.webOnboardingFinished,
      payload
    );
  }
}
export default EventManager;
